import request from '../utils/backend';
import env from '../environment';
import * as actions from '../constants/floatingTaskActions';
import * as logEntryActions from '../constants/logEntryActions';
import * as events from '../constants/eventActions';
import * as entityUtils from '../utils/entityUtils';

const floatingTaskActions = {

    create(floatingTask) {
        return dispatch => {
            dispatch(actions.createFloatingTask());

            // construct a pure floatingTask object which doesn't have unnecessary fields usually used for optimistical updates

            let pureFloatingTask = {};
            let blacklistedKeys = ["createdAt", "managerName"];

            for (let key in floatingTask) {
                if (blacklistedKeys.indexOf(key) === -1) {
                    pureFloatingTask[key] = floatingTask[key];
                }
            }

            request
                .post(env.tashi + '/floatingTasks/new')
                .accept('application/json')
                .withCredentials()
                .send(pureFloatingTask)
                .end((err, res) => {
                    if (err) {
                        dispatch(actions.createFloatingTask({ status: "failure", data: err }));
                        dispatch(events.pushErrorEvent({ data: "create floating task" }));
                        return;
                    }

                    if (res.ok) {
                        floatingTask.id = entityUtils.getIDFromLink(res.header.location); //we get the id of the newly-created task

                        dispatch(actions.createFloatingTask({ status: "success", data: floatingTask }));
                        dispatch(events.pushSuccessEvent({ data: "create floating task" }));
                    } else {
                        dispatch(actions.createFloatingTask({ status: "failure", data: res.status }));
                        dispatch(events.pushErrorEvent({ data: "create floating task" }));
                    }

                });
        };
    },

    createTimer(floatingTask, logEntry) {

         return dispatch => {
            dispatch(actions.createTimer());

            request
                .post(env.tashi + '/floatingTasks/' + floatingTask.id + '/newTimer')
                .withCredentials()
                .accept('application/json')
                .end((err, res) => {
                    if (err) {
                        dispatch(actions.createTimer({ status: "failure", data: err }));
                        dispatch(events.pushErrorEvent({ data: "start timing floating task" }));
                        return;
                    }

                    if (res.ok) {
                        //we need the ID of the newly created timed log entry
                        logEntry.id = entityUtils.getIDFromLink(res.header.location);

                        //will only the loading icon
                        dispatch(actions.createTimer({ status: "success" }));

                        //will put the new, timed log entry into store
                        dispatch(logEntryActions.createLogEntry({ status: "success", data: logEntry }));

                        dispatch(events.pushSuccessEvent({ data: "start timing floating task" }));
                    } else {
                        dispatch(actions.createTimer({ status: "failure", data: res.status }));
                        dispatch(events.pushErrorEvent({ data: "start timing floating task" }));
                    }
                });
        };
    },

    delete(floatingTaskID) {
        return dispatch => {
            dispatch(actions.deleteFloatingTask());

            request
                .post(env.tashi + '/floatingTasks/' + floatingTaskID + '/delete')
                .accept('application/json')
                .withCredentials()
                .end((err, res) => {
                    if (err) {
                        dispatch(actions.deleteFloatingTask({ status: "failure", data: err }));
                        dispatch(events.pushErrorEvent({ data: "delete floating task" }));
                        return;
                    }

                    if (res.ok) {
                        dispatch(actions.deleteFloatingTask({ status: "success", data: floatingTaskID }));
                        dispatch(events.pushSuccessEvent({ data: "delete floating task" }));
                    } else {
                        dispatch(actions.deleteFloatingTask({ status: "failure", data: res.status }));
                        dispatch(events.pushErrorEvent({ data: "delete floating task" }));
                    }
                });
        };
    },

    /*
     * Checks if the floating task is deletable, then if it is puts its id
     * into store. Used on Day view where the user can delete floating tasks.
     */
    isDeletable(floatingTaskID) {
        return dispatch => {
            request
                .get(env.tashi + '/tasks/isDeletable/' + floatingTaskID)
                .accept('application/json')
                .withCredentials()
                .end((err, res) => {
                    if (err) {
                        dispatch(actions.isDeletable({ status: "failure", data: err}));
                        return;
                    }

                    let data = JSON.parse(res.text);
                    //if the floating task is deletable, it will send the id of the floating task. Otherwise,
                    //it will send boolean "false"
                    if (data) {
                        dispatch(actions.isDeletable({ status: "success", data: {id: floatingTaskID, value: true}}));
                    } else {
                        dispatch(actions.isDeletable({ status: "success", data: {id: floatingTaskID, value: false}}));
                    }

                });
        };
    },

    invalidate(floatingTaskID) {
        return dispatch => {
            dispatch(actions.invalidateFloatingTask());

            request
                .patch(floatingTaskID)
                .accept('application/json')
                .send({
                    status: "REJECTED"
                })
                .withCredentials()
                .end((err, res) => {
                    if (err) {
                        dispatch(actions.invalidateFloatingTask({ status: "failure", data: err }));
                        return;
                    }

                    if (res.ok) {
                        dispatch(actions.invalidateFloatingTask({ status: "success", data: floatingTaskID }));
                    } else {
                        dispatch(actions.invalidateFloatingTask({ status: "failure", data: res.status }));
                    }
                });
        };
    },

    loadAll() {
        return dispatch => {
            dispatch(actions.loadAllFloatingTasks());

            request
                .get(env.tashi + '/floatingTasks/search/findAll')
                .accept('application/json')
                .withCredentials()
                .end((err, res) => {
                    if (err) {
                        dispatch(actions.loadAllFloatingTasks({ status: "failure", data: err }));
                        return;
                    }

                    let data = JSON.parse(res.text)._embedded ? JSON.parse(res.text)._embedded.floatingTasks : null;

                    if (data) {
                        dispatch(actions.loadAllFloatingTasks({ status: "success", data: data }));
                    } else {
                        dispatch(actions.loadAllFloatingTasks({ status: "success", data: [] }));
                    }

                });
        };
    },

    mergeExisting(floatingTaskID, taskID, quota) {
        return dispatch => {
            dispatch(actions.mergeFloatingTaskExisting());

            request
                .post(env.tashi + '/floatingTasks/' + floatingTaskID + '/merge')
                .query({
                    taskId: taskID,
                    quota: quota
                })
                .withCredentials()
                .accept('application/json')
                .end((err, res) => {
                    if (err) {
                        dispatch(actions.mergeFloatingTaskExisting({ status: "failure", data: err }));
                        dispatch(events.pushErrorEvent({ data: res.body || err.response.body || "merge existing floating task" }));
                        return;
                    }

                    if (res.status === 200) {
                        let data = {floatingTaskID, taskID};
                        dispatch(actions.mergeFloatingTaskExisting({ status: "success", data: data }));
                        dispatch(events.pushSuccessEvent({ data: "merge existing floating task" }));
                    } else {
                        dispatch(actions.mergeFloatingTaskExisting({ status: "failure", data: res.status }));
                        dispatch(events.pushWarningEvent({ data: res.body || err.response.body || "merge existing floating task" }));
                    }
                });
        };
    },

    mergeNew(floatingTaskID, task, quota) {
        return dispatch => {
            dispatch(actions.mergeFloatingTaskNew());

            request
                .post(env.tashi + '/floatingTasks/' + floatingTaskID + '/create')
                .send(task)
                .query({
                    quota: quota
                })
                .withCredentials()
                .accept('application/json')
                .end((err, res) => {
                    if (err) {
                        dispatch(actions.mergeFloatingTaskNew({ status: "failure", data: err }));
                        dispatch(events.pushErrorEvent({ data: res.body || err.response.body || "merge new floating task" }));
                        return;
                    }

                    if (res.status === 201) {
                        let data = {floatingTaskID, task, quota};
                        dispatch(actions.mergeFloatingTaskNew({ status: "success", data: data }));
                        dispatch(events.pushSuccessEvent({ data: "merge new floating task" }));
                    } else {
                        dispatch(actions.mergeFloatingTaskNew({ status: "failure", data: res.status }));
                        dispatch(events.pushWarningEvent({ data: res.body || err.response.body || "merge new floating task" }));
                    }
                });
        };
    },

    pmLoadAll() {
        return dispatch => {
            dispatch(actions.pmLoadAllFloatingTasks());

            request
                .get(env.tashi + '/floatingTasks/search/pm_findAll')
                .accept('application/json')
                .withCredentials()
                .end((err, res) => {
                    if (err) {
                        dispatch(actions.pmLoadAllFloatingTasks({ status: "failure", data: err }));
                        return;
                    }

                    let data = JSON.parse(res.text)._embedded.floatingTasks;

                    if (data) {
                        dispatch(actions.pmLoadAllFloatingTasks({ status: "success", data: data }));
                    } else {
                        dispatch(actions.pmLoadAllFloatingTasks({ status: "success", data: [] }));
                    }
                });
        };
    },

    pmoDelete(floatingTaskID) {
        return dispatch => {
            dispatch(actions.pmoDeleteFloatingTask());

            request
                .post(env.tashi + '/floatingTasks/' + floatingTaskID + '/pmo_delete')
                .accept('application/json')
                .withCredentials()
                .end((err, res) => {
                    if (err) {
                        dispatch(actions.pmoDeleteFloatingTask({ status: "failure", data: err }));
                        dispatch(events.pushErrorEvent({ data: "pmo delete floating task" }));
                        return;
                    }

                    if (res.ok) {
                        dispatch(actions.pmoDeleteFloatingTask({ status: "success", data: floatingTaskID }));
                        dispatch(events.pushSuccessEvent({ data: "pmo delete floating task" }));
                    } else {
                        dispatch(actions.pmoDeleteFloatingTask({ status: "failure", data: res.status }));
                        dispatch(events.pushErrorEvent({ data: "pmo delete floating task" }));
                    }
                });
        };
    },

    pmoLoadAll() {
        return dispatch => {
            dispatch(actions.pmoLoadAllFloatingTasks());

            request
                .get(env.tashi + '/floatingTasks/search/pmo_findAll')
                .accept('application/json')
                .withCredentials()
                .end((err, res) => {
                    if (err) {
                        dispatch(actions.pmoLoadAllFloatingTasks({ status: "failure", data: err }));
                        return;
                    }

                    let data = JSON.parse(res.text)._embedded.floatingTasks;

                    if (data) {
                        dispatch(actions.pmoLoadAllFloatingTasks({ status: "success", data: data }));
                    } else {
                        dispatch(actions.pmoLoadAllFloatingTasks({ status: "success", data: [] }));
                    }
                });
        };
    },

    reassign(floatingTaskID, manager, message) {
        return dispatch => {
            dispatch(actions.reassignFloatingTask());

            request
                .post(env.tashi + '/floatingTasks/' + floatingTaskID + '/reassign')
                .send({
                    manager: manager,
                    reassignMessage: message
                })
                .withCredentials()
                .accept('application/json')
                .end((err, res) => {
                    if (err) {
                        dispatch(actions.reassignFloatingTask({ status: "failure", data: err }));
                        dispatch(events.pushErrorEvent({ data: "reassign floating task" }));
                        return;
                    }

                    if (res.ok) {
                        let data = {floatingTaskID, manager, message};
                        dispatch(actions.reassignFloatingTask({ status: "success", data: data }));
                        dispatch(events.pushSuccessEvent({ data: "reassign floating task" }));
                    } else {
                        dispatch(actions.reassignFloatingTask({ status: "failure", data: res.status }));
                        dispatch(events.pushErrorEvent({ data: "reassign floating task" }));
                    }
                });
        };
    },

    returnToUser(floatingTaskID, message) {
        return dispatch => {
            dispatch(actions.returnFloatingTask());

            request
                .post(env.tashi + '/floatingTasks/' + floatingTaskID + '/return')
                .send({
                    returnMessage: message
                })
                .withCredentials()
                .accept('application/json')
                .end((err, res) => {
                    if (err) {
                        dispatch(actions.returnFloatingTask({ status: "failure", data: err }));
                        dispatch(events.pushErrorEvent({ data: "return floating tasks to user" }));
                        return;
                    }

                    if (res.ok) {
                        let data = {floatingTaskID, message};
                        dispatch(actions.returnFloatingTask({ status: "success", data: data }));
                        dispatch(events.pushSuccessEvent({ data: "return floating tasks to user" }));
                    } else {
                        dispatch(actions.returnFloatingTask({ status: "failure", data: res.status }));
                        dispatch(events.pushErrorEvent({ data: "return floating tasks to user" }));
                    }
                });
        };
    },

    validate(floatingTaskID) {
        return dispatch => {
            dispatch(actions.validateFloatingTask());

            request
                .patch(env.tashi + '/floatingTasks/' + floatingTaskID)
                .accept('application/json')
                .send({
                    status: "PENDING"
                })
                .withCredentials()
                .end((err, res) => {
                    if (err) {
                        dispatch(actions.validateFloatingTask({ status: "failure", data: err }));
                        return;
                    }

                    if (res.ok) {
                        dispatch(actions.validateFloatingTask({ status: "success", data: floatingTaskID }));
                    } else {
                        dispatch(actions.validateFloatingTask({ status: "failure", data: res.status }));
                    }
                });
        };
    }

};

export default floatingTaskActions;



/** WEBPACK FOOTER **
 ** ./src/app/actions/floatingTaskActionCreators.js
 **/